import { Smile, Zap } from "lucide-react"

interface MoodEnergyCardsProps {
  moodAverage: number | null
  energyAverage: number | null
}

const CIRCUMFERENCE = 2 * Math.PI * 28 // ≈ 175.9

interface RingChartProps {
  value: number
  color: string
}

function RingChart({ value, color }: RingChartProps) {
  const dashoffset = CIRCUMFERENCE * (1 - value / 5)

  return (
    <div
      className="mb-2 flex items-center justify-center"
      style={{ position: "relative" }}
    >
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle
          cx="36"
          cy="36"
          r="28"
          fill="none"
          stroke="var(--fig-ring-track)"
          strokeWidth="6"
        />
        <circle
          className="ring-progress"
          cx="36"
          cy="36"
          r="28"
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE.toFixed(1)}
          strokeDashoffset={dashoffset.toFixed(1)}
          transform="rotate(-90 36 36)"
        />
      </svg>
      <span style={{ position: "absolute", fontSize: 18, fontWeight: 700 }}>
        {value}
      </span>
    </div>
  )
}

interface MetricCardProps {
  icon: React.ReactNode
  label: string
  average: number | null
  goodColor: string
  okayColor: string
}

function MetricCard({
  icon,
  label,
  average,
  goodColor,
  okayColor,
}: MetricCardProps) {
  const isGood = average !== null && average >= 3.5
  const color =
    average !== null
      ? isGood
        ? goodColor
        : okayColor
      : "var(--fig-text-placeholder)"
  const qualityLabel = average !== null ? (isGood ? "Good" : "Okay") : null

  return (
    <div
      className="flex-1 rounded-2xl p-4"
      style={{
        background: "var(--fig-card-bg)",
        boxShadow: "var(--fig-card-shadow)",
        border: "1px solid var(--fig-card-border)",
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fig-text-inactive)",
          }}
        >
          {label}
        </span>
      </div>

      {average === null ? (
        <div className="flex flex-col items-center py-2">
          <span
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: "var(--fig-text-placeholder)",
            }}
          >
            —
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--fig-text-inactive)",
              marginTop: 4,
            }}
          >
            No data
          </span>
        </div>
      ) : (
        <>
          <RingChart value={average} color={color} />
          <div className="text-center">
            <span style={{ fontSize: 12, color, fontWeight: 600 }}>
              {qualityLabel}
            </span>
            <span
              style={{ fontSize: 11, color: "var(--fig-text-placeholder)" }}
            >
              {" "}
              / 5
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export function MoodEnergyCards({
  moodAverage,
  energyAverage,
}: MoodEnergyCardsProps) {
  return (
    <div className="insight-card flex gap-3">
      <MetricCard
        icon={
          <Smile size={16} style={{ color: "var(--fig-entry-mood-icon)" }} />
        }
        label="Mood avg"
        average={moodAverage}
        goodColor="var(--fig-mood-good)"
        okayColor="var(--fig-mood-okay)"
      />
      <MetricCard
        icon={<Zap size={16} style={{ color: "var(--fig-energy-good)" }} />}
        label="Energy avg"
        average={energyAverage}
        goodColor="var(--fig-energy-good)"
        okayColor="var(--fig-mood-okay)"
      />
    </div>
  )
}
